
import { useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { generateBunnyVideoUrl } from "@/lib/bunny";

interface BunnyPlayerProps {
  videoId: string;
  lessonId: string;
  title: string;
  poster?: string;
  onVideoEnd?: () => void;
  onVideoLoad?: () => void;
  onVideoError?: () => void;
}

const BunnyPlayer = ({
  videoId,
  lessonId,
  title,
  poster,
  onVideoEnd,
  onVideoLoad,
  onVideoError
}: BunnyPlayerProps) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const loadVideo = async () => {
      if (!videoId || !lessonId) {
        console.error("BunnyPlayer: missing videoId or lessonId", { videoId, lessonId });
        setError("No video ID provided");
        setIsLoading(false);
        if (onVideoError) onVideoError();
        return;
      }

      console.log("BunnyPlayer: Loading video", { videoId, lessonId });
      setIsLoading(true);
      setError(null);

      try {
        const url = await generateBunnyVideoUrl(videoId, lessonId);
        console.log("BunnyPlayer: Video URL generated successfully:", url.substring(0, 100) + "...");
        setVideoUrl(url);
        setIsLoading(false);
        if (onVideoLoad) onVideoLoad();
      } catch (err) {
        console.error("BunnyPlayer: Error loading video:", err);
        setError(err instanceof Error ? err.message : "Failed to load video");
        setIsLoading(false);
        if (onVideoError) onVideoError();
      }
    };
    
    loadVideo();
  }, [videoId, lessonId, onVideoLoad, onVideoError]);
  
  // Handle video end event
  const handleVideoEnd = () => {
    console.log("BunnyPlayer: Video ended");
    if (onVideoEnd) onVideoEnd();
  };
  
  if (isLoading) {
    return (
      <div className="w-full aspect-video bg-gray-100 rounded-lg">
        <Skeleton className="w-full h-full rounded-lg" />
      </div>
    );
  }
  
  if (error || !videoUrl) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error loading video</AlertTitle>
        <AlertDescription>{error || "Unknown error"}</AlertDescription>
      </Alert>
    );
  }
  
  console.log("BunnyPlayer: Rendering iframe with URL:", videoUrl.substring(0, 100) + "...");
  
  // Use iframe for Bunny.net video
  return (
    <div className="w-full rounded-lg overflow-hidden" style={{ position: "relative", paddingTop: "56.25%" }}>
      <iframe 
        src={videoUrl}
        title={title}
        style={{ 
          border: 0, 
          position: "absolute", 
          top: 0, 
          height: "100%", 
          width: "100%" 
        }}
        allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
        allowFullScreen={true}
        loading="lazy"
        onLoad={() => {
          console.log("BunnyPlayer: iframe loaded");
          if (onVideoLoad) onVideoLoad();
        }}
      />
    </div>
  );
};

export default BunnyPlayer;
